import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Plus, FileText, GitBranch, TestTube, Edit, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useRequirement,
  useUpdateRequirement,
  useCreateReqVersion,
  useCreateCoverage,
  useDeleteCoverage,
} from '@/features/requirements/api/useRequirements'
import { useTestCases } from '@/features/test-cases/api/useTestCases'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatDate } from '@/lib/utils'
import { logger } from '@/lib/utils/logger'
import {
  RequirementStatus,
  RequirementType,
  getRequirementStatusLabel,
  getRequirementStatusColor,
  getRequirementTypeLabel,
} from '@/types/requirement.types'

const RequirementDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requirementId = parseInt(id || '0', 10)

  const { data: requirement, isLoading } = useRequirement(requirementId)
  const { data: testCases } = useTestCases({})
  const updateMutation = useUpdateRequirement()
  const createVersionMutation = useCreateReqVersion()
  const createCoverageMutation = useCreateCoverage()
  const deleteCoverageMutation = useDeleteCoverage()

  // Edit state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [isEditingDocId, setIsEditingDocId] = useState(false)
  const [editedDocId, setEditedDocId] = useState('')

  // Version dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false)
  const [newVersionScope, setNewVersionScope] = useState('')
  const [newVersionStatus, setNewVersionStatus] = useState<RequirementStatus>(RequirementStatus.Draft)
  const [newVersionType, setNewVersionType] = useState<RequirementType>(RequirementType.Functional)

  // Coverage dialog state
  const [coverageDialogOpen, setCoverageDialogOpen] = useState(false)
  const [selectedVersionId, setSelectedVersionId] = useState<string>('')
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string>('')

  // Coverage delete confirmation
  const [deleteCoverageTarget, setDeleteCoverageTarget] = useState<{ id: number; name: string } | null>(null)

  // ── Inline edit handlers ────────────────────────────────────────────
  const handleSaveName = async () => {
    if (!editedName.trim() || !requirement) return
    try {
      await updateMutation.mutateAsync({
        id: requirement.id,
        data: { name: editedName.trim() },
      })
      toast.success('Name updated')
      setIsEditingName(false)
    } catch (err) {
      logger.error('Failed to update name', err)
      toast.error('Failed to update name')
    }
  }

  const handleSaveDocId = async () => {
    if (!editedDocId.trim() || !requirement) return
    try {
      await updateMutation.mutateAsync({
        id: requirement.id,
        data: { req_doc_id: editedDocId.trim() },
      })
      toast.success('Document ID updated')
      setIsEditingDocId(false)
    } catch (err) {
      logger.error('Failed to update document ID', err)
      toast.error('Failed to update document ID')
    }
  }

  // ── Version creation ────────────────────────────────────────────────
  const handleCreateVersion = async () => {
    if (!requirement || !newVersionScope.trim()) {
      toast.warning('Please enter the version scope')
      return
    }

    try {
      await createVersionMutation.mutateAsync({ reqId: requirement.id,
        
        data: {
          requirement_id: requirement.id,
          author_id: 1,
          scope: newVersionScope.trim(),
          status: newVersionStatus,
          type: newVersionType,
        },
      })
      toast.success('New version created')
      setVersionDialogOpen(false)
      setNewVersionScope('')
      setNewVersionStatus(RequirementStatus.Draft)
      setNewVersionType(RequirementType.Functional)
    } catch (err) {
      logger.error('Failed to create version', err)
      toast.error('Failed to create version. Please try again.')
    }
  }

  // ── Coverage management ─────────────────────────────────────────────
  const handleAddCoverage = async () => {
    if (!requirement || !selectedVersionId || !selectedTestCaseId) {
      toast.warning('Please select both a version and a test case')
      return
    }

    const version = requirement.versions?.find(
      (v) => v.id.toString() === selectedVersionId
    )
    if (!version) return

    try {
      await createCoverageMutation.mutateAsync({
        reqId: requirement.id,
        data: {
          req_id: requirement.id,
          req_version_id: version.id,
          testcase_id: parseInt(selectedTestCaseId, 10),
          tcversion_id: 1,
          author_id: 1,
        },
      })
      toast.success('Test case linked to requirement')
      setCoverageDialogOpen(false)
      setSelectedVersionId('')
      setSelectedTestCaseId('')
    } catch (err) {
      logger.error('Failed to link test case', err)
      toast.error('Failed to link test case. Please try again.')
    }
  }

  const handleConfirmDeleteCoverage = async () => {
    if (!deleteCoverageTarget) return
    try {
      await deleteCoverageMutation.mutateAsync({ reqId: requirement!.id, coverageId: deleteCoverageTarget.id })
      toast.success('Coverage link removed')
      setDeleteCoverageTarget(null)
    } catch (err) {
      logger.error('Failed to remove coverage', err)
      toast.error('Failed to remove coverage link')
    }
  }

  // ── Loading / Error states ──────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!requirement) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/requirements-ops/requirements')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Requirements
        </Button>
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              Requirement not found
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Main render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Navigation */}
      <Button variant="outline" onClick={() => navigate('/requirements-ops/requirements')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Requirements
      </Button>

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              {/* Editable Name */}
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="max-w-md"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName()
                      if (e.key === 'Escape') setIsEditingName(false)
                    }}
                  />
                  <Button size="sm" onClick={handleSaveName} disabled={updateMutation.isPending}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <CardTitle
                  className="cursor-pointer hover:text-blue-600 flex items-center gap-2"
                  onClick={() => {
                    setEditedName(requirement.name)
                    setIsEditingName(true)
                  }}
                >
                  <FileText className="h-5 w-5" />
                  {requirement.name}
                  <Edit className="h-3 w-3 text-muted-foreground" />
                </CardTitle>
              )}

              {/* Editable Doc ID */}
              {isEditingDocId ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedDocId}
                    onChange={(e) => setEditedDocId(e.target.value)}
                    className="max-w-xs"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveDocId()
                      if (e.key === 'Escape') setIsEditingDocId(false)
                    }}
                  />
                  <Button size="sm" onClick={handleSaveDocId} disabled={updateMutation.isPending}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingDocId(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <CardDescription
                  className="cursor-pointer hover:text-blue-600 flex items-center gap-1"
                  onClick={() => {
                    setEditedDocId(requirement.req_doc_id)
                    setIsEditingDocId(true)
                  }}
                >
                  <span className="font-mono">{requirement.req_doc_id}</span>
                  <Edit className="h-3 w-3" />
                </CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2">
              {requirement.type && (
                <Badge variant="outline">
                  {getRequirementTypeLabel(requirement.type as RequirementType)}
                </Badge>
              )}
              {requirement.status && (
                <Badge variant={getRequirementStatusColor(requirement.status as RequirementStatus)}>
                  {getRequirementStatusLabel(requirement.status as RequirementStatus)}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Specification:</span>
              <p className="font-medium">{requirement.srs_name || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Author:</span>
              <p className="font-medium">{requirement.author_name || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Created:</span>
              <p className="font-medium">{formatDate(requirement.created_at)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Modified:</span>
              <p className="font-medium">{formatDate(requirement.modified_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Versions Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                Versions ({requirement.versions?.length || 0})
              </CardTitle>
              <CardDescription>
                Track changes and scope across requirement revisions
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setVersionDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Version
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requirement.versions && requirement.versions.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revision</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirement.versions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>
                        <span className="font-mono text-sm">v{version.revision}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRequirementStatusColor(version.status)}>
                          {getRequirementStatusLabel(version.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {version.type ? (
                          <Badge variant="outline">
                            {getRequirementTypeLabel(version.type)}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm max-w-md line-clamp-2">
                          {version.scope || '—'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(version.creation_ts)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <GitBranch className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-muted-foreground">
                No versions yet. Create the first version to start tracking scope.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Test Coverage ({requirement.coverage?.length || 0})
              </CardTitle>
              <CardDescription>
                Test cases linked to this requirement for traceability
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setCoverageDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Link Test Case
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requirement.coverage && requirement.coverage.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test Case</TableHead>
                    <TableHead>Importance</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Linked By</TableHead>
                    <TableHead>Linked On</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirement.coverage.map((cov) => (
                    <TableRow
                      key={cov.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/tests/test-cases/${cov.testcase_id}`)}
                    >
                      <TableCell className="font-medium">
                        {cov.testcase_name || `Test Case #${cov.testcase_id}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {cov.importance === 1 ? 'High' : cov.importance === 2 ? 'Medium' : 'Low'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {cov.execution_type === 1 ? 'Manual' : cov.execution_type === 2 ? 'Automated' : '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {cov.author_name || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(cov.creation_ts)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteCoverageTarget({
                              id: cov.id,
                              name: cov.testcase_name || `Test Case #${cov.testcase_id}`,
                            })
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <TestTube className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-muted-foreground">
                No test cases linked yet. Link test cases to track coverage.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Version Dialog ─────────────────────────────────────────────── */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Add a new version to track requirement changes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="version-scope">Scope</Label>
              <Textarea
                id="version-scope"
                placeholder="Describe the scope of this version..."
                value={newVersionScope}
                onChange={(e) => setNewVersionScope(e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version-status">Status</Label>
                <Select
                  value={newVersionStatus}
                  onValueChange={(val) => setNewVersionStatus(val as RequirementStatus)}
                >
                  <SelectTrigger id="version-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RequirementStatus.Draft}>Draft</SelectItem>
                    <SelectItem value={RequirementStatus.Review}>In Review</SelectItem>
                    <SelectItem value={RequirementStatus.Valid}>Valid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="version-type">Type</Label>
                <Select
                  value={newVersionType}
                  onValueChange={(val) => setNewVersionType(val as RequirementType)}
                >
                  <SelectTrigger id="version-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RequirementType.Functional}>Functional</SelectItem>
                    <SelectItem value={RequirementType.NonFunctional}>Non-Functional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateVersion}
              disabled={createVersionMutation.isPending}
            >
              {createVersionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Version'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Coverage Dialog ────────────────────────────────────────────── */}
      <Dialog open={coverageDialogOpen} onOpenChange={setCoverageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Test Case</DialogTitle>
            <DialogDescription>
              Link a test case to this requirement for traceability
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="coverage-version">Requirement Version</Label>
              <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
                <SelectTrigger id="coverage-version">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  {requirement.versions?.map((version) => (
                    <SelectItem key={version.id} value={version.id.toString()}>
                      v{version.revision} — {getRequirementStatusLabel(version.status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coverage-testcase">Test Case</Label>
              <Select value={selectedTestCaseId} onValueChange={setSelectedTestCaseId}>
                <SelectTrigger id="coverage-testcase">
                  <SelectValue placeholder="Select test case" />
                </SelectTrigger>
                <SelectContent>
                  {testCases?.map((tc) => (
                    <SelectItem key={tc.id} value={tc.id.toString()}>
                      {tc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoverageDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCoverage}
              disabled={createCoverageMutation.isPending}
            >
              {createCoverageMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Linking...
                </>
              ) : (
                'Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coverage delete confirmation */}
      <ConfirmDialog
        open={!!deleteCoverageTarget}
        onOpenChange={(open) => { if (!open) setDeleteCoverageTarget(null) }}
        title="Remove Coverage Link"
        description={`Remove the link between this requirement and "${deleteCoverageTarget?.name}"? The test case itself will not be deleted.`}
        confirmLabel="Remove"
        variant="destructive"
        loading={deleteCoverageMutation.isPending}
        onConfirm={handleConfirmDeleteCoverage}
      />
    </div>
  )
}

export default RequirementDetailsPage
